/**
 * Multi-touch: the `touches` element (`.onTouches` in the shim, `onTouches`
 * in @swiftbrowser/react; docs/ops-protocol.md, "Multi-touch"). The
 * single-pointer gestures of src/interaction.ts own one pointer at a time,
 * which a piano or a drum pad cannot live with; this controller owns every
 * pointer that goes down inside a `touches` element and sends the element
 * `touches` events with all of them: `began` as one comes down, `changed` as
 * they move (one event per animation frame), `ended` as one lifts and
 * `cancelled` when the browser takes them away. Each event carries every
 * pointer still down, in the element's coordinates, so the app reads the
 * state from the event (the keys under the fingers) rather than pairing ups
 * with downs.
 *
 * A pointer down inside a `touches` element stops there: the interaction
 * controller never sees it, so no scroll view, swipe row or `gesture` above
 * the element competes for the finger, and the stylesheet's `touch-action:
 * none` keeps the browser from scrolling or zooming with it. Clicks still
 * reach the controls inside (a click is not a pointer event).
 *
 * `TouchSet` is the pure part: which pointers are down and what each change
 * reports, testable without a DOM.
 */

import type { DragPhase, ElementId, TouchPoint, TouchesEvent } from './protocol';

export interface TouchesHost {
  readonly root: HTMLElement;
  send(event: TouchesEvent): void;
}

const round = (n: number): number => Math.round(n * 100) / 100;

/** The pointers down on one `touches` element, by pointer id, in order of arrival. */
export class TouchSet {
  private readonly points = new Map<number, { x: number; y: number }>();

  get size(): number {
    return this.points.size;
  }

  has(id: number): boolean {
    return this.points.has(id);
  }

  /** A pointer came down: the set with it, as the event lists it. */
  down(id: number, x: number, y: number): TouchPoint[] {
    this.points.set(id, { x, y });
    return this.list();
  }

  /** A pointer moved; false when it is not one of ours or did not move. */
  move(id: number, x: number, y: number): boolean {
    const point = this.points.get(id);
    if (!point || (point.x === x && point.y === y)) return false;
    point.x = x;
    point.y = y;
    return true;
  }

  /** A pointer lifted: the set without it, or null when it was not one of ours. */
  up(id: number): TouchPoint[] | null {
    if (!this.points.delete(id)) return null;
    return this.list();
  }

  clear(): void {
    this.points.clear();
  }

  /** Every pointer down, rounded to hundredths of a point. */
  list(): TouchPoint[] {
    return [...this.points.entries()].map(([id, point]) => ({ id, x: round(point.x), y: round(point.y) }));
  }
}

/** One `touches` element with pointers down on it. */
interface Session {
  el: HTMLElement;
  id: ElementId;
  set: TouchSet;
  /** The element's box in the viewport, measured as each pointer comes down. */
  rect: DOMRect;
  /** A `changed` waiting for the next frame. */
  raf: number | null;
}

export class TouchesController {
  /** The elements with pointers down, and which element each pointer is on. */
  private readonly sessions = new Map<HTMLElement, Session>();
  private readonly owners = new Map<number, Session>();
  private listening = false;

  constructor(private readonly host: TouchesHost) {
    host.root.addEventListener('pointerdown', this.onPointerDown, true);
  }

  dispose(): void {
    this.host.root.removeEventListener('pointerdown', this.onPointerDown, true);
    this.detachWindow();
    for (const session of this.sessions.values()) {
      if (session.raf !== null) cancelAnimationFrame(session.raf);
    }
    this.sessions.clear();
    this.owners.clear();
  }

  /** The innermost `touches` element at or above `target`, or null. */
  private elementAt(target: EventTarget | null): { el: HTMLElement; id: ElementId } | null {
    const start = target instanceof Element ? target : null;
    const el = start?.closest<HTMLElement>('.sb-touches') ?? null;
    if (!el || !this.host.root.contains(el)) return null;
    // Pointer downs in a text field select text (as src/interaction.ts leaves them).
    if (start?.closest('input, textarea, [contenteditable]')) return null;
    const id = Number(el.dataset['sbId']);
    return Number.isInteger(id) ? { el, id } : null;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const found = this.elementAt(event.target);
    if (!found) return;
    // Ours alone: no gesture, scroll or swipe above competes for the pointer.
    event.stopPropagation();
    let session = this.sessions.get(found.el);
    if (!session) {
      session = { el: found.el, id: found.id, set: new TouchSet(), rect: found.el.getBoundingClientRect(), raf: null };
      this.sessions.set(found.el, session);
    } else {
      session.rect = found.el.getBoundingClientRect();
    }
    this.owners.set(event.pointerId, session);
    try {
      found.el.setPointerCapture(event.pointerId);
    } catch {
      // Not every environment allows capture; the window listeners still hear the pointer.
    }
    this.attachWindow();
    const touches = session.set.down(event.pointerId, event.clientX - session.rect.left, event.clientY - session.rect.top);
    this.flush(session);
    this.host.send({ type: 'touches', id: session.id, phase: 'began', touches });
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const session = this.owners.get(event.pointerId);
    if (!session) return;
    if (!session.set.move(event.pointerId, event.clientX - session.rect.left, event.clientY - session.rect.top)) return;
    if (session.raf !== null) return;
    session.raf = requestAnimationFrame(() => {
      session.raf = null;
      if (!this.sessions.has(session.el)) return;
      this.host.send({ type: 'touches', id: session.id, phase: 'changed', touches: session.set.list() });
    });
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    this.lift(event.pointerId, 'ended');
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    this.lift(event.pointerId, 'cancelled');
  };

  /** A pointer is gone: the element hears the set without it; the last one ends the session. */
  private lift(pointerId: number, phase: DragPhase): void {
    const session = this.owners.get(pointerId);
    if (!session) return;
    this.owners.delete(pointerId);
    const touches = session.set.up(pointerId);
    if (touches === null) return;
    this.flush(session);
    if (touches.length === 0) this.sessions.delete(session.el);
    if (this.owners.size === 0) this.detachWindow();
    this.host.send({ type: 'touches', id: session.id, phase, touches });
  }

  /** A `changed` waiting for the next frame goes out now, before a down or an up, so the order holds. */
  private flush(session: Session): void {
    if (session.raf === null) return;
    cancelAnimationFrame(session.raf);
    session.raf = null;
    this.host.send({ type: 'touches', id: session.id, phase: 'changed', touches: session.set.list() });
  }

  private attachWindow(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
  }

  private detachWindow(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
  }
}
